import React from "react";
import { Box, Chip, Typography, Skeleton } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export interface ShiftData {
  shiftId?: number;
  blockId?: number;
  shiftName?: string;
  availableClientCount: number;
  availableDays: number[];
  startTime: string;
  endTime: string;
  blockIds?: string;
  daysOfWeek: string;
  daysOfWeekShort: string;
  totalClientCount: number;
}

interface SetShiftCardsProps {
  shifts: ShiftData[];
  selectedDays?: (string | number[])[];
  selectedBlocks?: (string | number[])[];
  selectedShifts?: number[];
  onCardSelect?: (shiftId: number) => void;
  isLoading?: boolean;
}

export const SetShiftCards: React.FC<SetShiftCardsProps> = ({ 
  shifts, 
  selectedDays = [],
  selectedBlocks = [],
  selectedShifts = [],
  onCardSelect,
  isLoading = false
}) => {
  // Helper function to check if two arrays are equal
  const arraysEqual = (arr1: number[], arr2: number[]) => {
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort((a, b) => a - b);
    const sorted2 = [...arr2].sort((a, b) => a - b);
    return sorted1.every((val, index) => val === sorted2[index]);
  };

  // Helper function to normalize time for comparison
  const normalizeTime = (time: string): string => {
    // Convert "8:30 AM" or "8:30 am" to a standard format
    return time.trim().toUpperCase().replace(/\s+/g, ' ');
  };

  // Helper function to check if shift's time range matches the block's time range
  const matchesBlock = (shift: ShiftData, blockValue: string | number[]): boolean => {
    if (typeof blockValue !== 'string') return false;
    
    // Parse the block value "8:00 am - 1:30 pm"
    const [blockStart, blockEnd] = blockValue.split(' - ').map(normalizeTime);
    const shiftStart = normalizeTime(shift.startTime);
    const shiftEnd = normalizeTime(shift.endTime);
    
    // Check if the shift's time range matches the block's time range exactly
    return blockStart === shiftStart && blockEnd === shiftEnd;
  };

  // Filter shifts based on selected days and blocks
  const filteredShifts = shifts.filter((shift) => {
    // Check days filter
    const matchesDays = selectedDays.length === 0 || selectedDays.some((selectedDay) => {
      if (Array.isArray(selectedDay)) {
        return arraysEqual(shift.availableDays, selectedDay);
      }
      return false;
    });

    // Check blocks filter
    const matchesBlocks = selectedBlocks.length === 0 || selectedBlocks.some((selectedBlock) => {
      return matchesBlock(shift, selectedBlock);
    });

    // Shift must match both filters (if they are active)
    return matchesDays && matchesBlocks;
  });

  // Sort filtered shifts by availableClientCount descending
  const sortedShifts = [...filteredShifts].sort(
    (a, b) => b.availableClientCount - a.availableClientCount
  );

  // Render skeleton cards while loading
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
          },
          gap: { xs: 2, md: 2.5 },
          mt: { xs: 2, md: 3 },
        }}
      >
        {[1, 2, 3, 4].map((index) => {
          // First 2 cards are blue (highlighted)
          const isHighlighted = index <= 2;
          
          return (
            <Box
              key={index}
              sx={{
                position: "relative",
                padding: { xs: 2, sm: 2.5 },
                border: isHighlighted ? "1px solid #C4DBF7" : "1px solid #1212123B",
                borderRadius: "8px",
                backgroundColor: isHighlighted ? "#F2F7FD" : "#FFFFFF",
              }}
            >
              {/* Blue dot for highlighted cards */}
              {isHighlighted && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#10389A",
                    border: "1px solid #F2F7FD",
                  }}
                />
              )}

              {/* Days of Week Skeleton */}
              <Skeleton 
                variant="text" 
                width="60%" 
                height={24} 
                sx={{ mb: 1.5 }} 
              />

              {/* Time Range Skeleton */}
              <Skeleton 
                variant="rectangular" 
                width="70%" 
                height={32} 
                sx={{ mb: 1.5, borderRadius: "16px" }} 
              />

              {/* Client Count Skeleton */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton variant="text" width="50%" height={20} />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
        },
        gap: { xs: 2, md: 2.5 },
        mt: { xs: 2, md: 3 },
      }}
    >
      {sortedShifts.map((shift, index) => {
        // First 6 cards with available clients get light blue background
        const isHighlighted = index < 6 && shift.availableClientCount > 0;
        // Use blockId if shiftId doesn't exist
        const cardId = shift.shiftId || shift.blockId || 0;
        const isSelected = selectedShifts.includes(cardId);

        return (
      <Box
        key={cardId}
        onClick={(e) => {
          e.stopPropagation();
          onCardSelect?.(cardId);
        }}
        sx={{
          position: "relative",
          padding: { xs: 2, sm: 2.5 },
          border: isSelected
            ? "2px solid #10389A"
            : isHighlighted
              ? "1px solid #C4DBF7"
              : "1px solid #1212123B",
          borderRadius: "8px",
          backgroundColor: isHighlighted ? "#F2F7FD" : "#FFFFFF",
          cursor: "pointer",
        }}
      >
            {/* Checkmark for selected cards */}
        {isSelected && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                fontSize: "30px",
                color: "#10389A",
              }}
            />
          </Box>
        )}

            {/* Blue dot indicator for highlighted cards (only if not selected) */}
            {isHighlighted && !isSelected && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "#10389A",
                  border: "1px solid #F2F7FD",
                }}
              />
            )}

            {/* Days of Week */}
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "15px", sm: "16px" },
                fontWeight: 600,
                color: "#000000",
                mb: 1.5,
                pr: 2,
              }}
            >
              {shift.daysOfWeek} {`(${shift.daysOfWeekShort.toUpperCase()})`}
            </Typography>

            {/* Time Range */}
            <Chip
              icon={
                <AccessTimeIcon
                  sx={{
                    fontSize: { xs: "16px", sm: "18px" },
                    color: "#666666 !important",
                  }}
                />
              }
              label={`${shift.startTime} - ${shift.endTime}`}
              sx={{
                mb: 1.5,
                "& .MuiChip-label": {
                  fontSize: { xs: "13px", sm: "14px" },
                  color: "#666666",
                },
              }}
            />

            {/* Client Count */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <GroupIcon
                sx={{
                  fontSize: { xs: "16px", sm: "18px" },
                  color: isHighlighted ? "#10389A" : "#666666",
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "13px", sm: "14px" },
                  color: isHighlighted ? "#10389A" : "#666666",
                  fontWeight: isHighlighted ? 600 : 400,
                }}
              >
                {shift.totalClientCount}{" "}
                {shift.totalClientCount === 1 ? "client" : "clients"} waiting
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
